/**
 * ECharts Tooltip 生命周期补丁（模块加载时执行一次）。
 *
 * ## 结论
 *
 * 根因已定位到 ECharts 上游 bug（https://github.com/apache/echarts/issues/19827），
 * 不是业务 formatter 写错。hide/dispose 时未取消 pending 的 show 定时器，导致
 * setContent 在 el 已为 null 时写 innerHTML 崩溃。
 *
 * ## 为什么要这个文件？
 *
 * ECharts 显示 tooltip 不是同步的，内部大致流程：
 *
 *   鼠标移动 / updateAxisPointer
 *     → TooltipView._showOrMove
 *     → setTimeout(showDelay)   // 默认或配置里的 showDelay，如 5ms
 *     → _showTooltipContent
 *     → TooltipHTMLContent.setContent  // 往 el.innerHTML 写 HTML
 *
 * 隐藏或销毁时：
 *
 *   globalout / hideTip / 图表 dispose
 *     → manuallyHideTip 或 dispose
 *     → TooltipHTMLContent.dispose 会把 this.el 设为 null
 *
 * 问题在于：echarts@5.x 在 hide/dispose 时 **没有** clearTimeout(_showTimout)，
 * 之前排队的「延迟显示」回调仍会执行，此时 el 已是 null，就会报：
 *
 *   Cannot set properties of null (setting 'innerHTML')
 *
 * 详见同目录上级 docs/tooltip-lifecycle-incident.md
 *
 * ## 本文件的策略（三层防护）
 *
 * 1. hide / dispose 前：取消尚未执行的 show 定时器（治本）
 * 2. setContent：若 el 已被销毁则直接 return（兜底，防止漏网）
 * 3. 配合 Chart/index.tsx 在 setOption 前 dispatchAction hideTip（wrapper 层）
 *
 * ## 为何改 prototype（猴子补丁）？
 *
 * 根因在上游、无公开 API 可清 _showTimout，在 Chart 公共层单点 patch 是成本最低的
 * 防御手段；所有经 Chart/index.tsx 创建的实例共用同一套 ECharts 类，在 echarts.use
 * 之前 patch 一次即可覆盖 Datalab 全部图表，无需每个图表单独处理。升级 ECharts 时
 * 须对照 #19827 评估是否可移除本补丁。
 */
import TooltipHTMLContent from 'echarts/lib/component/tooltip/TooltipHTMLContent';
import TooltipView from 'echarts/lib/component/tooltip/TooltipView';

/**
 * TooltipView 内部用到的两个定时器（ECharts 私有字段，无公开 API）。
 *
 * - _showTimout：_showOrMove 里「延迟显示 tooltip」的 timer（注意 ECharts 源码拼写是 Timout）
 * - _refreshUpdateTimeout：setOption 后 _keepShow 里「下一帧再 manuallyShowTip」的 timer
 */
type TTooltipViewWithTimers = {
  _showTimout?: ReturnType<typeof setTimeout> | null;
  _refreshUpdateTimeout?: ReturnType<typeof setTimeout> | null;
};

/** 保证全应用只 patch 一次，避免重复包裹 prototype 方法 */
let patched = false;

/**
 * 取消 TooltipView 上尚未执行的异步 show 相关定时器。
 * 在 tooltip 即将隐藏或图表即将销毁时调用，避免「旧回调在新 DOM 上写内容」。
 */
const cancelTooltipPendingTimers = (view: TTooltipViewWithTimers): void => {
  if (view._showTimout != null) {
    clearTimeout(view._showTimout);
    view._showTimout = null;
  }
  if (view._refreshUpdateTimeout != null) {
    clearTimeout(view._refreshUpdateTimeout);
    view._refreshUpdateTimeout = null;
  }
};

/**
 * 对 ECharts Tooltip 相关 prototype 打补丁。
 * 由 Chart/index.tsx 在模块顶层调用；SSR 环境（无 window）下跳过。
 */
export const patchEchartsTooltipLifecycle = (): void => {
  if (patched || typeof window === 'undefined') {
    return;
  }
  patched = true;

  // ── 补丁 1：setContent 空 el 防护（最后一道防线）──
  const contentProto = TooltipHTMLContent.prototype;
  const originalSetContent = contentProto.setContent;
  contentProto.setContent = function setContentSafe(
    this: TooltipHTMLContent,
    ...args: Parameters<TooltipHTMLContent['setContent']>
  ): void {
    // dispose 后 el 为 null；若仍有延迟回调跑到这里，静默跳过即可，不应抛错
    if (!this.el) {
      return;
    }
    originalSetContent.apply(this, args);
  };

  // ── 补丁 2 & 3：hide / dispose 前取消 pending timer（对齐 ECharts 应有行为）──
  const viewProto = TooltipView.prototype;

  // hideTip / globalout 等会走到 manuallyHideTip；原实现只 hide DOM，不清 _showTimout
  const originalManuallyHideTip = viewProto.manuallyHideTip;
  viewProto.manuallyHideTip = function manuallyHideTipPatched(...args: unknown[]): void {
    cancelTooltipPendingTimers(this as TTooltipViewWithTimers);
    originalManuallyHideTip.apply(this, args);
  };

  // 图表 unmount、echarts.dispose 时会销毁 tooltip DOM；必须先清 timer 再 dispose
  const originalDispose = viewProto.dispose;
  viewProto.dispose = function disposePatched(...args: unknown[]): void {
    cancelTooltipPendingTimers(this as TTooltipViewWithTimers);
    originalDispose.apply(this, args);
  };
};
